import {Component, createRef, Fragment, RefObject} from "react";
import * as d3 from "d3";
import { ts } from "src/utils/translate"
import {GroupedElement, GroupNameByUgroupIdRequest} from "src/model/api/grouped-elements.model";
import {getFrontendURL} from "src/utils/url";

interface GroupedElementsListState {
  editedItem: boolean;
  editedItemId: any;
  editedName: any;
  editedOldName: any;
  visibleRef: boolean;
  width: number;
  height: number;
  rasterSize: number;
  fontSizes: {
    /**
     * Size of port name texts on devices.
     */
    portName: number;
    /**
     * Size of text that's on/after a cable coming out from a port.
     */
    portAdditionalText: number;
    /**
     * Size of text that's on/after a cable coming out from a menorah group port.
     */
    menorahAdditionalText: number;
    /**
     * Size of text that's on/after a cable coming out from a port on an IT device.
     */
    itAdditionalText: number;
    /**
     * Size of a cable's name.
     */
    cableName: number;
    /**
     * Size of a tree bus line's name, that's before the line on a tree bus page (eg. "Line 1").
     */
    busLineText: number;
    /**
     * Size of a tree bus item's description text. Eg. "Living Room<br>Telephone".
     */
    busEntity: number;
    busEntitySymbol: number;
    /**
     * The text that tells the customers that this is not a 100% correct plan.
     */
    safetyText: number;
    itNameText: number;
    /**
     * The text under resistors (usually their resistance)
     */
    resistorText: number;
    portNLabelName: number;
    deviceName: number;
    cableTypeName: number;
  },
  margin: number;
  devExtraMargin: number;
  /**
   * The space reserved for the label text before tree bus lines (eg. "Line 1").
   */
  busLineTextWidth: number;
  /**
   * Number of lines below each other on tree bus pages.
   */
  busLinesPerPage_old: number;
  busLinesPerPage: number;
  /**
   * Width of an entity on a bus.
   */
  busEntitySize: number;
  /**
   * Width and height of a bus entity icon
   */
  busIconSize: number;
  /**
   * Distance of tree devices.
   */
  busDistance: number;
  busTextToLineDist: number;
  busLineToWireDist: number;
  /**
   * Distance of (non-tree) devices.
   */
  deviceDistance: number;
  /**
   * Stroke width of cables
   */
  cableStroke: number,
  /**
   * Vertical distance of cables.
   */
  pinoutDevCablesDist: number;
  /**
   * Vertical distance of the uppermost cable and the bottom of the devices.
   */
  cableDevSpace: number;
  /**
   * The space between cables on a device's left side (where the upper ports connect to bottom cables).
   */
  leftCableSpace: number;
  /**
   * The default space between cables coming out of ports and the ports' labels.
   */
  defaultPortLabelDistance: number;
  /**
   * The length of the short "cable" coming out of a port when it's connected to a plain text.
   */
  portPlainTextDistance: number;
  /**
   * The vertical difference of grouped ports' lines.
   */
  menorahDistanceIncrementY: number;
  deviceNameMargin: {
    top: number;
    bottom: number;
  },
  portNameMargin: {
    top: number,
    bottom: number;
    left: number;
    right: number;
  },
  labelNameMargin: {
    left: number;
    right: number;
  },
  data: []
}

interface GroupedElementsListProps {
  groupedDevicesData: GroupedElement[];
  width: number;
  height: number;
  saveGroupedName: (data: GroupNameByUgroupIdRequest) => void;
  projectId: string;
}


export class GroupedElementsList extends Component<GroupedElementsListProps, GroupedElementsListState> {

  private readonly svgContainer: RefObject<any>;

  constructor(props: GroupedElementsListProps) {
    super(props);

    const rasterSize = 8;
    this.state = {
      editedItem: false,
      editedItemId: null,
      editedName: null,
      editedOldName: null,
      visibleRef: false,
      width : 842,
      height: 595,
      rasterSize: rasterSize,
      fontSizes: {
        portName: rasterSize * 0.75,
        portAdditionalText: rasterSize * 0.9,
        menorahAdditionalText: rasterSize * 0.75,
        itAdditionalText: rasterSize * 1.2,
        cableName: rasterSize * 1,
        busLineText: rasterSize * 1.4,
        busEntity: rasterSize * 1.1,
        busEntitySymbol: rasterSize * 0.95,
        safetyText: rasterSize,
        itNameText: rasterSize * 1.5,
        resistorText: rasterSize * 0.9,
        portNLabelName: rasterSize * 1.5,
        deviceName: rasterSize * 2,
        cableTypeName: rasterSize * 1.25,
      },
      margin : rasterSize * 3,
      devExtraMargin : rasterSize,
      busLineTextWidth : 60,
      busLinesPerPage_old : 5,
      busLinesPerPage : 5,
      busEntitySize : rasterSize * 5,
      busIconSize : rasterSize * 4,
      busDistance : rasterSize * 1,
      busTextToLineDist : rasterSize * 2,
      busLineToWireDist : rasterSize * 1,
      deviceDistance : rasterSize * 1,
      cableStroke : 1,
      pinoutDevCablesDist : rasterSize * 2,
      cableDevSpace : rasterSize * 2 /* * 15 */,
      leftCableSpace : rasterSize,
      defaultPortLabelDistance : rasterSize * 1.5,
      portPlainTextDistance : rasterSize * 2,
      menorahDistanceIncrementY : rasterSize / 2,
      deviceNameMargin : {
        top: rasterSize,
        bottom: rasterSize
      },
      portNameMargin : {
        top: 0,
        bottom: rasterSize * 2,
        left: rasterSize,
        right: rasterSize * 2
      },
      labelNameMargin : {
        left: rasterSize * 2,
        right: rasterSize * 2
      },
      data: []
    };
    this.handleChange = this.handleChange.bind(this);
    this.handleSubmit = this.handleSubmit.bind(this);
    this.svgContainer = createRef();
  }

  componentDidMount() {
    if (this.props.groupedDevicesData) {
      this.drawElement();
    }
  }

  componentDidUpdate(prevProps: any, prevState: GroupedElementsListState) {
    const {groupedDevicesData} = this.props;

    if (prevProps.groupedDevicesData !== groupedDevicesData) {
      this.drawElement();
    }

    if (prevState.editedItem !== this.state.editedItem) {
      this.drawElement();
    }
  }

  render() {
    return <Fragment>
      <div ref={this.svgContainer}></div>
    </Fragment>
  }

  private createSvgContainer()
  {
    const { width, height } = this.state;
    d3.select(this.svgContainer.current).html("");
    d3.select(this.svgContainer.current).text("");
    let cont = d3.select(this.svgContainer.current).insert("svg")
    .attr("width", '100%')
    .attr("height", '100%')
    .attr("viewBox", `0 0 ${ width } ${ height }`)
    .attr("class", "pinout lightshadow")
    .append("g");

    this.appendDefsToSVG(cont);
    this.insertSafetyText(cont);
    return cont;
  }

  private appendDefsToSVG(svg: any) {
    svg.append('defs')
    .append('marker')
    .attr('id', 'head')
    .attr('orient', 'auto')
    .attr('markerWidth', '4')
    .attr('markerHeight', '4')
    .attr('refX', '0.1')
    .attr('refY', '2')
    .append('path')
    .attr('d', 'M0,0 V4 L4,2 Z')
    .attr('fill', 'black')
    return svg;
  }

  private insertSafetyText(container: any) {
    const {width, margin, fontSizes} = this.state;
    container.insert("text")
      .text(ts('q_safety_text', "This is not an actual electrical plan. Please contact your electrician for guidance."))
      .attr("font-size", fontSizes.safetyText)
      .attr("x", width - margin * 2)
      .attr("y", margin / 2 + fontSizes.safetyText / 2)
      .attr("text-anchor", "end")
      .attr("fill", "lightgrey");
  }

  private safeGetBBox(svgElement: any) {
    let tempDiv = document.createElement('div');
    tempDiv.setAttribute('style', "position:absolute; visibility:hidden; width:0; height:0");
    document.body.appendChild(tempDiv);
    let tempSvg = document.createElementNS("http://www.w3.org/2000/svg", 'svg');
    tempDiv.appendChild(tempSvg);
    let tempEl = svgElement.cloneNode(true);
    tempSvg.appendChild(tempEl);
    let bb = tempEl.getBBox();
    document.body.removeChild(tempDiv);
    return bb;
  }

  private editItem(item: any)
  {
    this.setState({
      'editedItem' : true,
      'editedItemId' : item.ugroup_id,
      'editedName' : item.name,
      'editedOldName' : item.name,
    });
  }

  private handleChange(event: any)
  {
    this.setState({
      'editedName' : event.target.value
    });
  }

  private handleCancel()
  {
    this.setState({
      'editedItem' : false,
      'editedItemId' : null,
      'editedName' : null,
      'editedOldName' : null,
    });
  }

  private handleSubmit(event: any) {
    const {editedItemId, editedName, editedOldName} = this.state;
    if(editedName === editedOldName) {
      this.handleCancel();
      return;
    }
    this.props.saveGroupedName({
      ugroup_id: editedItemId,
      name: editedName,
      project_id: this.props.projectId
    } as GroupNameByUgroupIdRequest);
    this.handleCancel();
  }

  private drawElement()
  {
    const { groupedDevicesData } = this.props;
    const { editedItem, editedItemId } = this.state;

    if (this.svgContainer.current) {
      if (groupedDevicesData) {
        let cont = null;

        if (!Object.keys(groupedDevicesData).length) {
          return;
        }

        const imgSize = 26;

        const originalYPos = 60;
        const originalXPos = 10;

        let YPos = originalYPos;
        let XPos = originalXPos;

        const {margin, busEntitySize, fontSizes, busLineTextWidth, cableStroke} = this.state;

        const linePlusMarginRight = {
          x: margin + busLineTextWidth - 5,
          y: YPos + busEntitySize / 2 + fontSizes.busLineText / 2 * 0.7
        };

        cont = this.createSvgContainer();

        for (const [index, group] of Object.entries(groupedDevicesData) as [any, any]) {
          if (editedItem === false || (editedItem && editedItemId !== group.ugroup_id)) {
            const deviceNameText = cont.append("text")
            .text(group.name)
            .attr("x", linePlusMarginRight.x - 45)
            .attr("y", YPos)
            .attr("font-size", fontSizes.deviceName)
            .attr('text-anchor', 'start').node();

            const deviceNameTextBox = this.safeGetBBox(deviceNameText);

            const editImage = cont.append('svg:image')
              .attr('x', deviceNameTextBox.x + deviceNameTextBox.width)
              .attr('y', YPos - imgSize + 6)
              .attr('xlink:href', `${getFrontendURL()}/images/edit.svg`)
              .attr('width', imgSize)
              .attr('height', imgSize)
              .style('cursor', 'pointer');

            editImage.on("click", (item) => {
              this.editItem(group)
            });
          }
          else {

            const deviceNameText = cont.append("text")
            .text(group.name)
            .attr("x", linePlusMarginRight.x - 45)
            .attr("y", YPos)
            .attr('id', 'asdqwe')
            .attr("font-size", fontSizes.deviceName)
            .attr('text-anchor', 'start').node();

            const deviceNameTextBox = this.safeGetBBox(deviceNameText);

            const inputText = cont.append("foreignObject")
            .attr("x", linePlusMarginRight.x - 45)
            .attr("y", YPos - 10)
            .attr("width", deviceNameTextBox.width)
            .attr("height", deviceNameTextBox.height)
            .append('xhtml:input')
            .attr('type', 'text')
            .attr('value', this.state.editedName).node() as HTMLInputElement | null;

            inputText?.addEventListener('input', this.handleChange);

            const okImage = cont.append('svg:image')
              .attr('x', deviceNameTextBox.x + deviceNameTextBox.width + 5)
              .attr('y', YPos - 12)
              .attr('xlink:href', `${getFrontendURL()}/images/ok.svg`)
              .attr('width', imgSize)
              .attr('height', imgSize)
              .style('cursor', 'pointer');

            okImage.on("click", this.handleSubmit);

            const cancelImage = cont.append('svg:image')
              .attr('x', deviceNameTextBox.x + deviceNameTextBox.width + imgSize + 10)
              .attr('y', YPos - 12)
              .attr('xlink:href', `${getFrontendURL()}/images/cancel.svg`)
              .attr('width', imgSize)
              .attr('height', imgSize)
              .style('cursor', 'pointer');

            cancelImage.on("click", (item) => {
              this.handleCancel();
            });

            cont.select('#asdqwe').remove();
          }

          YPos += 15;

          let elementCounter = 1;

          for (const [groupedElementIndex, groupedElement] of Object.entries(group.items) as [any, any]) {
            cont.append('svg:image')
              .attr('x', linePlusMarginRight.x + XPos - (imgSize / 2))
              .attr('y', YPos)
              .attr('xlink:href', `${getFrontendURL()}${groupedElement.icon}`)
              .attr('width', imgSize)
              .attr('height', imgSize);

            cont.append("text")
            .text(groupedElement.name)
            .attr("x", linePlusMarginRight.x + XPos)
            .attr("y", YPos + imgSize + 10)
            .attr("font-size", fontSizes.cableName)
            .attr('text-anchor', 'middle');

            XPos += 55;

            elementCounter += 1;

            if (XPos > this.props.width - (linePlusMarginRight.x + originalXPos)) {
              elementCounter = 1;
              YPos = YPos + imgSize + 35;
              XPos = originalXPos;
            }

          }

          XPos = originalXPos;
          YPos += 75;

          if (YPos > this.props.height) {
            cont = this.createSvgContainer();
            YPos = originalYPos;
          }

        }
      }
    }
  }
}
